import {useCallback, useRef, type RefCallback} from 'react'

interface Focusable {
  focus(): void
}

/**
 * If the target container unmounts while focus is inside of (or on) it, this hook will return focus to the target.
 */
export function useReturnFocus() {
  const returnFocusRef = useRef<Focusable>(null)

  const containerRef = useCallback((container: HTMLElement | null) => {
    return () => {
      // Timeout allows for cases where the return focus target renders in place of the container
      if (container?.contains(document.activeElement)) setTimeout(() => returnFocusRef.current?.focus())
    }
  }, [])

  return {
    /**
     * Attach this ref the container around all the focuseable elements (or the focuseable target itself if there is
     * only one). This container **must** be conditionally rendered; focus will only be returned when this item is
     * unmounted.
     */
    containerRef,
    /**
     * Attach to the item to return focus to. **Must be focusable and remain (or become) mounted when the container
     * unmounts.**
     *
     * @warning This is not actually a callback ref, don't attempt to call it at runtime.
     */
    // Here we lie to the typechecker to make this ref easier to use without an explicit type declaration. Ref objects
    // can only be assigned to supertype targets, but ref callbacks can be assigned to subtype targets. This is safe as
    // long as the ref is only used in places that accept the `Ref` type.
    returnFocusRef: returnFocusRef as unknown as RefCallback<Focusable>,
  }
}
